import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { CheckCircle, Info, Error as ErrorIcon } from '@mui/icons-material';
import LanguageToggle from 'components/LanguageToggle';
import { useTranslation } from 'react-i18next';


const OneClickEnrollment = () => {
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); 
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const request = searchParams.get('request');
    const decodedRequest = request ? atob(request) : null;
    const parsedRequest = decodedRequest ? JSON.parse(decodedRequest) : null;
    const { t } = useTranslation();

    useEffect(() => {
        const handleEnrollment = async () => {
            try {
                const response = await fetch('/api/application/one-click/apply', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(parsedRequest),
                });

                const data = await response.json();

                if (response.ok) {
                    setStatus('success');
                } else {
                    if (data.error && data.error.includes('school_id must be unique')) {
                        setStatus('already_applied');
                    } else {
                        setStatus('error');
                    }
                }
            } catch (error) {
                console.error('Enrollment error:', error);
                setStatus('error');
            } finally {
                setLoading(false);
            }
        };

        handleEnrollment();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" width={'100%'}>
                <Box textAlign="center">
                    <CircularProgress size={60} />
                    <Typography variant="body1" sx={{ mt: 2 }}>
{                        t('Processing your application...')
}                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: '40px 20px', maxWidth: '800px', margin: '0 auto' }}>
            {status === 'success' && (
                <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        {t('Successfully Applied!')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('Your Application has been submitted successfully.')}
                    </Typography>
                </Paper>
            )}

            {status === 'already_applied' && (
                <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
                    <Info sx={{ fontSize: 80, color: 'info.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        {t('Already Applied')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('You have already submitted an application for this school.')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button variant="contained" onClick={() => navigate('/application-status')}>
                            {t('View Your Application Status')}
                        </Button>
                    </Box>
                </Paper>
            )}

            {status === 'error' && (
                <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
                    <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        {t('Enrollment Failed')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t('Sorry, we could not process your application. Please try again later.')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button variant="contained" onClick={() => window.location.reload()}>
                            {t('Try Again')}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('/application-status')}>
                            {t('View Your Application Status')}
                        </Button>
                    </Box>
                </Paper>
            )}
            <Paper sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1300 }}>
                <LanguageToggle />
            </Paper>
        </Box>
    );
};

export default OneClickEnrollment;

